import React, { useState } from 'react';
import { Zap, Check, DollarSign, Truck, CreditCard, Clock, Package, Shield, Phone, Mail } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { generators, generatorFeatures, deliveryInfo, allGeneratorSizes, generatorTypes } from '../mock';
import { toast } from 'sonner';

const GeneratorTool = () => {
  const [selectedGenerator, setSelectedGenerator] = useState(null);

  const handleSelectGenerator = (generator) => {
    setSelectedGenerator(generator);
    toast.success("Generator Selected", {
      description: `${generator.name} selected. Scroll down for delivery details and to request a quote.`,
      duration: 4000,
    });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToContact = () => {
    scrollToSection('contact');
  };

  return (
    <section id="generator-tool" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-6">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Generator Solutions
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-4">
            Premium Cummins diesel generators - Delivered to your doorstep in Jamaica
          </p>
          <Button
            onClick={() => scrollToSection('generator-promotion')}
            variant="outline"
            className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            View Current Promotion & All Available Sizes
          </Button>
        </div>

        {/* Generator Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {generators.map((generator) => (
            <Card
              key={generator.id}
              className={`cursor-pointer transition-all duration-300 transform hover:-translate-y-2 ${
                selectedGenerator?.id === generator.id
                  ? 'border-2 border-blue-600 shadow-xl bg-blue-50'
                  : 'border-2 border-gray-200 hover:border-blue-300 hover:shadow-lg'
              }`}
              onClick={() => handleSelectGenerator(generator)}
            >
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <Zap
                    className={`w-8 h-8 ${
                      selectedGenerator?.id === generator.id ? 'text-blue-600' : 'text-gray-400'
                    }`}
                  />
                  {selectedGenerator?.id === generator.id && (
                    <Check className="w-6 h-6 text-blue-600" />
                  )}
                </div>
                <CardTitle className="text-lg text-gray-900">{generator.name}</CardTitle>
                <CardDescription className="text-sm font-semibold text-blue-600">
                  {generator.power}
                </CardDescription>
                <CardDescription className="text-xs text-gray-500">
                  {generator.standby}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm mb-4">{generator.description}</p>
                <div className="mb-4">
                  <div className="text-2xl font-bold text-gray-900">{generator.price}</div>
                  <div className="text-xs text-gray-500 mt-1">Delivered to Jamaica</div>
                </div>
                <ul className="space-y-1">
                  {generator.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-xs text-gray-600">
                      <Check className="w-3 h-3 text-blue-600 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Delivery & Payment Information */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Delivery Timeline */}
          <Card className="border-2 border-gray-200">
            <CardHeader>
              <div className="flex items-center mb-2">
                <Truck className="w-6 h-6 text-blue-600 mr-3" />
                <CardTitle className="text-xl">Delivery Timeline</CardTitle>
              </div>
              <CardDescription>Your generator delivered to your doorstep</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {deliveryInfo.timeline.map((item, idx) => (
                  <div key={idx} className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-blue-600 font-semibold text-sm">{idx + 1}</span>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{item.step}</div>
                      <div className="text-sm text-gray-600">{item.days}</div>
                    </div>
                  </div>
                ))}
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center text-blue-600 font-semibold">
                    <Clock className="w-5 h-5 mr-2" />
                    Total: Approximately 65 days
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Method */}
          <Card className="border-2 border-gray-200">
            <CardHeader>
              <div className="flex items-center mb-2">
                <CreditCard className="w-6 h-6 text-blue-600 mr-3" />
                <CardTitle className="text-xl">Payment & Terms</CardTitle>
              </div>
              <CardDescription>Flexible payment options available</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start">
                  <DollarSign className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
                  <div>
                    <div className="font-semibold text-gray-900">{deliveryInfo.payment.deposit}</div>
                    <div className="text-sm text-gray-600">To confirm your order</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <Package className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
                  <div>
                    <div className="font-semibold text-gray-900">{deliveryInfo.payment.balance}</div>
                    <div className="text-sm text-gray-600">Before final delivery to doorstep</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <Shield className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
                  <div>
                    <div className="font-semibold text-gray-900">{deliveryInfo.payment.warranty}</div>
                    <div className="text-sm text-gray-600">Protection for your investment</div>
                  </div>
                </div>
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <CreditCard className="h-5 w-5 text-blue-600" />
                    <span className="text-sm font-medium text-gray-900">Payment Options Available</span>
                  </div>
                  <p className="text-xs text-gray-500">We accept major credit cards and bank transfers</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Features & Configuration */}
        <Card className="border-2 border-gray-200 mb-12">
          <CardHeader>
            <CardTitle className="text-2xl text-center">Features & Configuration</CardTitle>
            <CardDescription className="text-center">All generators include premium components</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              {generatorFeatures.map((feature, idx) => (
                <div key={idx} className="flex items-start">
                  <Check className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{feature}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Contact for Generator Inquiries */}
        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Ready to Power Your Business?</h3>
              <p className="text-gray-600">Contact us for more information or to place your order</p>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <div className="flex items-center">
                <Phone className="w-5 h-5 text-blue-600 mr-2" />
                <span className="font-semibold text-gray-900">876-479-7139 / 876-784-7936</span>
              </div>
              <div className="flex items-center">
                <Mail className="w-5 h-5 text-blue-600 mr-2" />
                <span className="font-semibold text-gray-900">qncconstructions@gmail.com</span>
              </div>
            </div>
            <div className="flex justify-center">
              <Button
                onClick={scrollToContact}
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Request a Quote
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Full Promotion Section - All Sizes and Types */}
      <div id="generator-promotion" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 scroll-mt-24">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Complete Generator Range
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            We offer generators in all power sizes and configurations to meet your specific needs
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* All Power Sizes */}
          <Card className="border-2 border-gray-200">
            <CardHeader>
              <div className="flex items-center mb-2">
                <Zap className="w-6 h-6 text-blue-600 mr-3" />
                <CardTitle className="text-2xl">All Power Sizes Available</CardTitle>
              </div>
              <CardDescription>From small residential to large industrial applications</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {allGeneratorSizes.map((size, idx) => (
                  <div key={idx} className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-blue-50 transition-colors duration-200">
                    <Check className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-700">{size}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-700 text-center font-semibold">
                  Custom sizes available upon request
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Generator Types */}
          <Card className="border-2 border-gray-200">
            <CardHeader>
              <div className="flex items-center mb-2">
                <Package className="w-6 h-6 text-blue-600 mr-3" />
                <CardTitle className="text-2xl">Generator Types</CardTitle>
              </div>
              <CardDescription>Various configurations to suit your installation requirements</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {generatorTypes.map((type, idx) => (
                  <div key={idx} className="flex items-start p-3 bg-gray-50 rounded-lg hover:bg-blue-50 transition-colors duration-200">
                    <Check className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-700">{type}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-700 text-center font-semibold">
                  All types can be customized to your specifications
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Brands and Options */}
        <Card className="border-2 border-gray-200 mb-12">
          <CardHeader>
            <CardTitle className="text-2xl text-center">Premium Brands & Options</CardTitle>
            <CardDescription className="text-center">We source and supply generators from world-leading manufacturers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-gray-50 rounded-xl">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Engine Brands</h3>
                <p className="text-sm text-gray-600">Cummins, Perkins, Volvo, Deutz, MTU, Doosan, and more</p>
              </div>
              <div className="text-center p-6 bg-gray-50 rounded-xl">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Package className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Alternator Brands</h3>
                <p className="text-sm text-gray-600">Stamford, Leroy Somer, Marathon, Mecc Alte, and more</p>
              </div>
              <div className="text-center p-6 bg-gray-50 rounded-xl">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Control Systems</h3>
                <p className="text-sm text-gray-600">Deep Sea, ComAp, SmartGen, Datakom, and more</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Us for Custom Requirements */}
        <Card className="border-2 border-blue-600 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
          <CardContent className="pt-8 pb-8">
            <div className="text-center">
              <h3 className="text-3xl font-bold mb-4">Need a Custom Generator Solution?</h3>
              <p className="text-lg mb-6 text-blue-100">
                Contact us for pricing, technical specifications, and availability for any generator size or type
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-8">
                <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-lg px-6 py-3">
                  <Phone className="w-6 h-6 mr-3" />
                  <div className="text-left">
                    <div className="text-xs text-blue-100">Call Us</div>
                    <div className="font-bold">876-479-7139</div>
                    <div className="font-bold">876-784-7936</div>
                  </div>
                </div>
                <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-lg px-6 py-3">
                  <Mail className="w-6 h-6 mr-3" />
                  <div className="text-left">
                    <div className="text-xs text-blue-100">Email Us</div>
                    <div className="font-bold">qncconstructions@gmail.com</div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  onClick={scrollToContact}
                  size="lg"
                  className="bg-white text-blue-600 hover:bg-gray-100 px-8 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Request Custom Quote
                </Button>
                <Button
                  onClick={() => window.open('tel:8764797139')}
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 px-8"
                >
                  Call Now
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default GeneratorTool;